import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createClinic } from "../../store/thunks/clinicThunk";
import { logInfo, logError } from "../../utils/logger";
import AddressFields, { validateZipCode, getAddressLabels } from "../../applicationUi/components/AddressFields";

const AddClinicModal = ({ isOpen, onClose, onClinicCreated }) => {
  const dispatch = useDispatch();
  const [saveStatus, setSaveStatus] = useState("initial");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdClinic, setCreatedClinic] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    website: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "United States",
    },
    hoursOfOperation: {
      Monday: { open: "09:00", close: "17:00" },
      Tuesday: { open: "09:00", close: "17:00" },
      Wednesday: { open: "09:00", close: "17:00" },
      Thursday: { open: "09:00", close: "17:00" },
      Friday: { open: "09:00", close: "17:00" },
      Saturday: { open: "", close: "" },
      Sunday: { open: "", close: "" },
    },
    taxRate: 0.13,
    currency: "CAD",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    const { regionLabel, postalLabel } = getAddressLabels(formData.address.country);

    // Required fields validation
    if (!formData.name.trim()) newErrors.name = "Clinic name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required";
    if (!formData.address.street.trim())
      newErrors["address.street"] = "Street address is required";
    if (!formData.address.city.trim())
      newErrors["address.city"] = "City is required";
    if (!formData.address.state.trim())
      newErrors["address.state"] = `${regionLabel} is required`;

    // Country-aware postal code validation
    const postalError = validateZipCode(formData.address.zipCode, formData.address.country);
    if (postalError) newErrors["address.zipCode"] = postalError;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Website validation - allow URLs with or without protocol
    if (formData.website && formData.website.trim()) {
      const websiteValue = formData.website.trim();
      // Check if it's a valid domain format (with or without protocol)
      const domainRegex = /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!domainRegex.test(websiteValue)) {
        newErrors.website = "Please enter a valid website URL or domain";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleHoursChange = (day, type, value) => {
    setFormData((prev) => ({
      ...prev,
      hoursOfOperation: {
        ...prev.hoursOfOperation,
        [day]: {
          ...prev.hoursOfOperation[day],
          [type]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaveStatus("saving");
    try {
      const result = await dispatch(createClinic(formData)).unwrap();
      logInfo("Clinic created successfully", {
        clinicId: result._id,
        clinicName: result.name,
      });
      setSaveStatus("saved");
      setCreatedClinic(result);
      setShowSuccessModal(true);

      // Auto-close success modal after 2 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
        onClose();
        if (onClinicCreated) {
          onClinicCreated(result);
        }
        resetForm();
      }, 2000);
    } catch (error) {
      logError("Failed to create clinic", { error });
      setSaveStatus("error");

      // Handle specific error cases
      if (error.includes("duplicate") || error.includes("unique")) {
        if (error.toLowerCase().includes("name")) {
          setErrors({ name: "A clinic with this name already exists" });
        } else if (error.toLowerCase().includes("email")) {
          setErrors({ email: "A clinic with this email already exists" });
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      website: "",
      address: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "United States",
      },
      hoursOfOperation: {
        Monday: { open: "09:00", close: "17:00" },
        Tuesday: { open: "09:00", close: "17:00" },
        Wednesday: { open: "09:00", close: "17:00" },
        Thursday: { open: "09:00", close: "17:00" },
        Friday: { open: "09:00", close: "17:00" },
        Saturday: { open: "", close: "" },
        Sunday: { open: "", close: "" },
      },
      taxRate: 0.13,
      currency: "CAD",
    });
    setErrors({});
    setSaveStatus("initial");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // Success Modal
  if (showSuccessModal && createdClinic) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Clinic Created Successfully!
          </h3>
          <p className="text-sm text-gray-600">
            {createdClinic.name} has been added to your clinics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Clinic
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Create a new clinic in your network
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clinic Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`block w-full px-3 py-2 text-sm border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                    errors.name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter clinic name"
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`block w-full px-3 py-2 text-sm border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                    errors.email ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="clinic@example.com"
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  className={`block w-full px-3 py-2 text-sm border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                    errors.phoneNumber ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="(555) 123-4567"
                />
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => handleInputChange("website", e.target.value)}
                  className={`block w-full px-3 py-2 text-sm border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                    errors.website ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="https://www.example.com or example.com"
                />
                {errors.website && (
                  <p className="text-sm text-red-600 mt-1">{errors.website}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">Address</h4>
            <AddressFields
              address={formData.address}
              onChange={(field, value) => handleInputChange(`address.${field}`, value)}
              errors={errors}
            />
          </div>

          {/* Hours of Operation */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">
              Hours of Operation
            </h4>
            <div className="space-y-3">
              {Object.entries(formData.hoursOfOperation).map(([day, hours]) => (
                <div key={day} className="flex items-center space-x-4">
                  <div className="w-24">
                    <span className="text-sm font-medium text-gray-700">
                      {day}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 flex-1">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Open:</label>
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) =>
                          handleHoursChange(day, "open", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Close:</label>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) =>
                          handleHoursChange(day, "close", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                    {(!hours.open || !hours.close) && (
                      <span className="text-sm text-gray-500 italic">Closed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              Leave times empty for closed days
            </p>
          </div>

          {/* Business Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">
              Business Settings
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Rate
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formData.taxRate}
                  onChange={(e) =>
                    handleInputChange(
                      "taxRate",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="0.13"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter as decimal (e.g., 0.13 for 13%)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent">
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveStatus === "saving"}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                saveStatus === "saving"
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : saveStatus === "saved"
                  ? "bg-green-600 text-white"
                  : saveStatus === "error"
                  ? "bg-red-600 text-white"
                  : "bg-gray-900 text-white hover:bg-gray-800"
              }`}>
              {saveStatus === "saving"
                ? "Creating..."
                : saveStatus === "saved"
                ? "Created!"
                : saveStatus === "error"
                ? "Error - Try Again"
                : "Create Clinic"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClinicModal;
